import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, PlusCircle, Database, Target, LogOut, Rocket, Radio, GraduationCap, BookOpen, Users, Mic, Phone, Mail, Settings2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { NotificationsBell } from "./NotificationsBell";
import { FollowUpBadge } from "./FollowUpBadge";
import { PageTransition } from "./PageTransition";

// Sezioni del menu. `feature` = modulo che l'admin può abilitare/disabilitare per utente.
// `mobile` = presente nella barra inferiore su smartphone.
export const SECTIONS = [
  { id: "home", label: "Panoramica", items: [
    { to: "/", label: "Dashboard", short: "Home", icon: LayoutDashboard, id: "nav-dashboard", mobile: true },
    { to: "/followup", label: "Follow-up", short: "Follow-up", icon: Mail, id: "nav-followup", badge: true },
    { to: "/impostazioni", label: "Impostazioni", short: "Impostazioni", icon: Settings2, id: "nav-settings" },
  ] },
  { id: "sales_help", label: "Sales Help", feature: "sales_help", items: [
    { to: "/aggiungi", label: "Aggiungi contenuto", short: "Aggiungi", icon: PlusCircle, id: "nav-add", mobile: true },
    { to: "/conoscenza", label: "Knowledge Base", short: "Knowledge", icon: Database, id: "nav-knowledge", mobile: true },
    { to: "/contesto", label: "Contesto", short: "Contesto", icon: Target, id: "nav-context" },
    { to: "/manuale", label: "Manuale script", short: "Manuale", icon: BookOpen, id: "nav-manual", mobile: true },
    { to: "/coach", label: "Coach", short: "Coach", icon: GraduationCap, id: "nav-coach", mobile: true },
    { to: "/live", label: "Assistente Live", short: "Live", icon: Radio, id: "nav-live", accent: true, mobile: true },
  ] },
  { id: "mypailot", label: "MyPailot", feature: "mypailot", items: [
    { to: "/mypailot", label: "Voci operatori", short: "MyPailot", icon: Mic, id: "nav-mypailot", mobile: true },
  ] },
  { id: "chiamate", label: "Chiamate", feature: "chiamate", items: [
    { to: "/chiamate", label: "Chiamate vocali AI", short: "Chiamate", icon: Phone, id: "nav-calls", mobile: true },
  ] },
  { id: "admin", label: "Amministrazione", adminOnly: true, items: [
    { to: "/utenti", label: "Utenti", short: "Utenti", icon: Users, id: "nav-users", mobile: true },
  ] },
];

// Sezioni visibili per l'utente corrente (feature abilitate + ruolo)
export const visibleSections = (user) =>
  SECTIONS.filter((s) => (s.adminOnly ? user?.role === "admin" : !s.feature || user?.features?.[s.feature] !== false));

const linkCls = (isActive, accent) =>
  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${
    isActive ? "bg-primary/15 text-white border border-primary/30" : "text-muted-foreground hover:bg-secondary hover:text-white"
  } ${accent ? "border border-dashed border-[#F5B942]/40" : ""}`;

export const Layout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const doLogout = () => {
    logout();
    navigate("/");
  };

  const sections = visibleSections(user);
  // Barra inferiore mobile: una voce per sezione (Home, Help, MyPailot, Call, Utenti solo admin)
  const MOBILE_TABS = { home: { to: "/", short: "Home", icon: LayoutDashboard, id: "nav-dashboard" },
    sales_help: { to: "/help", short: "Help", icon: Radio, id: "nav-help", accent: true, match: ["/help", "/aggiungi", "/conoscenza", "/contenuti", "/contesto", "/manuale", "/coach", "/live"] },
    mypailot: { to: "/mypailot", short: "MyPailot", icon: Mic, id: "nav-mypailot" },
    chiamate: { to: "/chiamate", short: "Call", icon: Phone, id: "nav-calls" },
    admin: { to: "/utenti", short: "Utenti", icon: Users, id: "nav-users" } };
  const mobileItems = sections.map((s) => MOBILE_TABS[s.id]).filter(Boolean);

  return (
    <div className="min-h-screen overflow-x-clip md:flex">
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 border-r border-border bg-card/60 p-6">
        <Brand />
        <nav data-testid="sidebar-nav" className="mt-6 flex flex-1 flex-col gap-3.5 overflow-y-auto">
          {sections.map((s) => (
            <div key={s.id} data-testid={`nav-section-${s.id}`}>
              <p className="mb-1 px-3 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70">{s.label}</p>
              <div className="flex flex-col gap-1">
                {s.items.map(({ to, label, icon: Icon, id, accent, badge }) => (
                  <NavLink key={to} to={to} end={to === "/"} data-testid={id} className={({ isActive }) => linkCls(isActive, accent)}>
                    <Icon className={`h-4 w-4 ${accent ? "text-[#FCD990]" : ""}`} />
                    {label}
                    {badge && <FollowUpBadge />}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <NotificationsBell />
        <div className="mt-2 rounded-lg border border-border px-3 py-2">
          <p data-testid="current-user-email" className="truncate text-xs font-medium text-white">{user?.email}</p>
          <p className="text-[11px] text-muted-foreground">{isAdmin ? "Amministratore" : "Venditore"}</p>
        </div>
        <button data-testid="logout-btn" onClick={doLogout} className="mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-white">
          <LogOut className="h-4 w-4" /> Esci
        </button>
      </aside>

      <header className="glass sticky top-0 z-30 flex items-center justify-between border-b border-white/10 px-4 py-3 md:hidden">
        <Brand compact />
        <div className="flex items-center gap-1">
          <FollowUpBadge compact />
          <NotificationsBell compact />
          <button data-testid="logout-btn-mobile" onClick={doLogout} className="rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-white" title="Esci">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="min-w-0 flex-1 overflow-x-clip md:ml-64 px-4 pb-28 pt-6 sm:px-8 sm:py-10 lg:px-12 md:pb-10">
        <div className="mx-auto max-w-6xl">
          <PageTransition>
            <Outlet />
          </PageTransition>
        </div>
      </main>

      <nav data-testid="mobile-bottom-nav" className="glass fixed inset-x-0 bottom-0 z-30 grid border-t border-white/10 px-1 pb-[max(env(safe-area-inset-bottom),8px)] pt-2 md:hidden"
        style={{ gridTemplateColumns: `repeat(${Math.max(mobileItems.length, 1)}, minmax(0, 1fr))` }}>
        {mobileItems.map(({ to, short, icon: Icon, id, accent, match }) => {
          const isActive = to === "/" ? pathname === "/" : (match || [to]).some((p) => pathname.startsWith(p));
          return (
            <NavLink key={to} to={to} data-testid={`${id}-mobile`} aria-current={isActive ? "page" : undefined}
              className={`flex flex-col items-center gap-1 rounded-lg py-1.5 text-[10px] font-medium transition-colors ${isActive ? "text-white" : "text-muted-foreground"}`}>
              <span className={`flex h-8 w-10 items-center justify-center rounded-full transition-colors ${isActive ? (accent ? "bg-[#F5B942]/30" : "bg-primary/25") : ""}`}>
                <Icon className={`h-[18px] w-[18px] ${accent && !isActive ? "text-[#FCD990]" : ""}`} />
              </span>
              {short}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};

const Brand = ({ compact }) => (
  <div className="flex items-center gap-3">
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary shadow-[0_0_24px_rgba(37,99,235,0.45)]">
      <Rocket className="h-5 w-5 text-white" />
    </div>
    <div>
      <p className="text-sm font-bold leading-tight">Sales<span className="text-primary">Pailot</span></p>
      {!compact && <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Sales Assistant</p>}
    </div>
  </div>
);
